import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = anon, object = authed
    const [booting, setBooting] = useState(true);

    const refreshMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            // 401 unauthenticated, 403 deactivated → treat as logged-out
            setUser(false);
        } finally {
            setBooting(false);
        }
    }, []);

    useEffect(() => {
        refreshMe();
    }, [refreshMe]);

    const login = async (identifier, password) => {
        try {
            const { data } = await api.post("/auth/login", { identifier, password });
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    const register = async ({ name, email, phone, password }) => {
        try {
            const body = { name, password };
            if (email) body.email = email;
            if (phone) body.phone = phone;
            const { data } = await api.post("/auth/register", body);
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {
            /* noop */
        }
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, booting, login, register, logout, refreshMe, setUser }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
